import { ethers } from 'ethers'
import { QuoteStaleError } from '../errorMessages'
import { buildKyberTx } from '../kyber'
import type { Adapter, ExecuteArgs } from './types'

/**
 * KyberSwap's own router, whichever address it names in the build response.
 *
 * The address is not hardcoded on purpose: the aggregator routes through
 * several executors and tells us which one this trade uses. That is also why
 * the approval is read from the same response — approving the wrong router is
 * a transaction that reverts after the user has already paid to approve.
 *
 * Everything else is identical to the on-chain adapters: the caller dry-runs
 * this transaction before a wallet prompt, and reports what arrived from the
 * balance rather than from the quote.
 */
export const kyberAdapter: Adapter = {
  spender(args: ExecuteArgs): string | null {
    if (args.tokenIn.isNative) return null
    return args.quote.kyber?.routerAddress ?? null
  },
  // With the platform router in the path the execution hook overrides the
  // spender: the wallet approves our router, never KyberSwap's directly.

  async buildTx(args: ExecuteArgs): Promise<ethers.PopulatedTransaction> {
    const built = await buildKyberTx({
      quote: args.quote,
      account: args.account,
      // The gateway validates this range again; sending what the user set is
      // the only place it is decided.
      settings: { slippageBps: args.slippageBps, deadlineMinutes: 0 },
      deadline: args.deadline,
      // Through the platform router the router pays KyberSwap; the wallet still receives.
      sender: args.routerAddress ?? undefined
    })
    /*
     * The one place a stale quote is visible before it costs anything.
     *
     * The on-chain venues enforce the floor themselves, so a price that moved
     * comes back as a revert the dry run catches. KyberSwap builds its own
     * transaction and prices it again while doing so — which means it can hand
     * back a route that no longer clears the floor the user was shown, and
     * only this comparison would notice.
     */
    if (built.amountOut < args.minOut) throw new QuoteStaleError()

    return {
      to: built.to,
      data: built.data,
      value: ethers.BigNumber.from(built.value.toString())
    }
  }
}
